import { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { DatePicker } from "@mui/x-date-pickers"
import { Button } from "@mui/material"
import { ArrowDropDown, OfflineBolt } from "@mui/icons-material"
import { get } from "lodash"
import moment from "moment"
import Tooltip from "components/material/tooltip"
import { DATE } from "helpers/shapes"
import { formatLongDate } from "helpers/formatters/dates"
import { DEFAULT_PROPS, DEFAULT_SLOT_PROPS } from "helpers/date-picker"
import "./style.scss"

export default function OPDatePicker({ selectedDate: originalDate, earliestDate, latestDate, adjusted, exclusionMap, onChange }) {
  const [minDate, maxDate, selectedDate] = useMemo(
    () => [moment(earliestDate), moment(latestDate), moment(originalDate)],
    [earliestDate, latestDate, originalDate],
  )

  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(false)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [currentDate, setCurrentDate] = useState(selectedDate)

  const shouldDisableDate = useCallback((date) => {
    // exclude weekends
    if (date.isoWeekday() > 5) {
      return true
    } else if (exclusionMap) {
      return !!get(exclusionMap, date.format("YYYY-MM-DD"))
    } else {
      return false
    }
  }, [exclusionMap])

  const handleToggleEditing = () => {
    if (!editing) {
      setCalendarOpen(true)
    }
    setEditing(!editing)
  }

  const reset = () => {
    setCalendarOpen(false)
    setEditing(false)
    setError(null)
  }

  const handleCancel = () => {
    setCurrentDate(selectedDate)
    reset()
  }

  const handleSet = () => {
    onChange({ selectedDate: currentDate.format("YYYY-MM-DD") })
    reset()
  }

  const handleSetCurrentDate = (date, { validationError }) => {
    if (!date) {
      validationError = "required"
    }
    setCurrentDate(date)
    setError(validationError)
  }

  return <div className={classNames("components_date-picker", { adjusted })}>
    {editing ? <div className="custom-date">
      <DatePicker
        {...DEFAULT_PROPS}
        minDate={minDate}
        maxDate={maxDate}
        value={currentDate}
        open={calendarOpen}
        onChange={handleSetCurrentDate}
        onOpen={() => setCalendarOpen(true)}
        onClose={() => setCalendarOpen(false)}
        shouldDisableDate={shouldDisableDate}
        slotProps={{
          ...DEFAULT_SLOT_PROPS,
          textField: {
            size: "small",
          },
          openPickerButton: {
            "data-test-class": "CalendarIcon",
          },
        }}
      />
      <Button onClick={handleCancel} variant="contained" size="small">
        Cancel
      </Button>
      <Button onClick={handleSet} color="primary" variant="contained" size="small" disabled={!!error}>
        Set
      </Button>
    </div> : <a className="link" onClick={handleToggleEditing}>
      {adjusted && <Tooltip title="The date has been adjusted to reflect data availability">
        <OfflineBolt className="warning" />
      </Tooltip>}
      <span>{formatLongDate(selectedDate)}</span>
      <ArrowDropDown />
    </a>}
  </div>
}

OPDatePicker.propTypes = {
  selectedDate: DATE.isRequired,
  earliestDate: DATE.isRequired,
  latestDate: DATE.isRequired,
  exclusionMap: PropTypes.objectOf(PropTypes.bool),
  onChange: PropTypes.func.isRequired,
  adjusted: PropTypes.bool,
}
